import { useDispatch, useSelector } from 'react-redux';
import './journal.scss';
import { useEffect, useState, useMemo } from 'react';
import { fetchBillets } from '../../data/slices/billetsSlice';
import UpdatedTable from '../../components/updated-table/StyledTable';
import { 
    Search as SearchIcon, 
    FileDownloadOutlined, 
    Refresh, 
    ShoppingCart,
    FilePresent
} from "@mui/icons-material";
import { COLORS, jsonToXlsx } from '../../settings/themeSettings';
import { 
    Typography, 
    Box, 
    Button, 
    TextField, 
    Paper, 
    Stack, 
    InputAdornment,
    Chip,
    CircularProgress,
    Grid
} from '@mui/material';
import { BlobProvider } from '@react-pdf/renderer';
import JournalVentePDF from '../../components/JournalPDF/JournalVentePDF';
import PageHeader from '../../components/layout/PageHeader';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';

const normalizeString = (str) => {
    return (str || '').toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const fuzzyMatch = (query, text) => {
    if (!query || !text) return false;
    const q = normalizeString(query);
    const t = normalizeString(text);
    if (t.includes(q)) return true;
    let idx = 0;
    for (const char of q) {
        idx = t.indexOf(char, idx);
        if (idx === -1) return false;
        idx++;
    }
    return true;
};

const Journal = (props) => {
    const { billets, fetchBilletsStatus } = useSelector((state) => state.billets);
    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredBillets, setFilteredBillets] = useState([]);

    useEffect(() => {
        dispatch(fetchBillets());
    }, [dispatch]);

    useEffect(() => {
        if (!!startDate && !!endDate) {
            dispatch(fetchBillets({ startDate, endDate }));
        }
    }, [startDate, endDate, dispatch]);

    // Recherche multicritères en temps réel
    useEffect(() => {
        if (!searchQuery) {
            setFilteredBillets(billets);
            return;
        }

        const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
        const filtered = billets.filter(billet => {
            const fullText = [
                billet.numero, billet.dateVol, billet.dateVolRetour,
                billet.etat, billet.classe, billet.classeRetour,
                billet.nom, billet.trajet,
                billet.tarif?.toString(), billet.montantAchat?.toString(),
                billet.marge?.toString()
            ].join(' ');
            return searchTerms.every(term => fuzzyMatch(term, fullText));
        });

        setFilteredBillets(filtered);
    }, [searchQuery, billets]);

    const billetsToDisplay = searchQuery ? filteredBillets : billets;

    const stats = useMemo(() => {
        const totalVente = billetsToDisplay.reduce((sum, b) => sum + (Number(b.tarif) || 0), 0);
        const totalAchat = billetsToDisplay.reduce((sum, b) => sum + (Number(b.montantAchat) || 0), 0);
        const totalMarge = billetsToDisplay.reduce((sum, b) => sum + (Number(b.marge) || 0), 0);
        return { totalVente, totalAchat, totalMarge };
    }, [billetsToDisplay]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader 
                title={props.title || "Journal de Vente"}
                subtitle="Suivez et analysez toutes vos émissions de billets"
                icon={ShoppingCart}
                breadcrumbs={[
                    { label: 'Journaux', path: '/journal' },
                    { label: 'Vente Billetterie', path: '/journal/vente' }
                ]}
                actions={[
                    { 
                        label: 'Exporter Excel', 
                        icon: <FileDownloadOutlined />, 
                        onClick: () => jsonToXlsx(billetsToDisplay, "Journal de vente"),
                        variant: 'outlined',
                        color: 'primary'
                    }
                ]}
            />

            {/* KPI Cards */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
                <Paper elevation={0} sx={{ p: 3, flex: 1, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                    <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Total Ventes</Typography>
                    <Typography variant="h4" sx={{ color: COLORS.secondary, fontWeight: 800 }}>{stats.totalVente.toLocaleString()} CFA</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 3, flex: 1, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                    <Typography variant="subtitle2" sx={{ color: COLORS.text.secondary, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Total Achats</Typography>
                    <Typography variant="h4" sx={{ color: COLORS.secondary, fontWeight: 800 }}>{stats.totalAchat.toLocaleString()} CFA</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 3, flex: 1, borderRadius: '16px', border: '1px solid', borderColor: COLORS.success + '30', bgcolor: COLORS.success + '05' }}>
                    <Typography variant="subtitle2" sx={{ color: COLORS.success, fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>Marge Totale</Typography>
                    <Typography variant="h4" sx={{ color: COLORS.success, fontWeight: 800 }}>{stats.totalMarge.toLocaleString()} CFA</Typography>
                </Paper>
            </Stack>

            {/* Filters & Search */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: COLORS.gray[200], bgcolor: 'white' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Rechercher : PNR, client, trajet..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: COLORS.gray[400] }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Grid>
                    
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Début"
                                value={startDate}
                                onChange={(val) => setStartDate(val)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Fin"
                                value={endDate}
                                onChange={(val) => setEndDate(val)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>
                    </LocalizationProvider>

                    <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button 
                                variant="text" 
                                startIcon={<Refresh />} 
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                    setSearchQuery('');
                                    dispatch(fetchBillets());
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Réinitialiser
                            </Button>
                            
                            <BlobProvider document={<JournalVentePDF data={[...billetsToDisplay].reverse()} headSubs={{ "numero": "numero billet/PNR", "dateVol": "date vol Aller", "tarif": "Montant Vente" }}
                                heads={['numero', 'dateVol', 'etat', 'classe', 'nom', 'trajet', 'montantAchat', 'tarif', 'marge']} title="Journal de Vente" />}>
                                {({ loading, url }) => (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <FilePresent />}
                                        href={url}
                                        target="_blank"
                                        disabled={loading}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                                    >
                                        Imprimer PDF
                                    </Button>
                                )}
                            </BlobProvider>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <Paper elevation={0} sx={{ 
                borderRadius: '16px', 
                overflow: 'hidden', 
                border: '1px solid',
                borderColor: COLORS.gray[200],
                bgcolor: 'white'
            }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: COLORS.gray[100], bgcolor: COLORS.gray[50] }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Détails des émissions ({billetsToDisplay.length})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Chip label="Confirmé" size="small" sx={{ bgcolor: COLORS.success + '20', color: COLORS.success, fontWeight: 700 }} />
                        <Chip label="En attente" size="small" sx={{ bgcolor: COLORS.warning + '20', color: COLORS.warning, fontWeight: 700 }} />
                    </Stack>
                </Box>
                
                {fetchBilletsStatus === 'loading' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                        <CircularProgress color="primary" />
                        <Typography sx={{ mt: 2, color: COLORS.text.secondary }}>Chargement des données...</Typography>
                    </Box>
                ) : (
                    <UpdatedTable
                        rows={[...billetsToDisplay].reverse()}
                        head={['numero', 'dateVol', 'dateVolRetour', 'etat', 'classe', 'classeRetour', 'nom', 'trajet', 'tarif', 'montantAchat', 'marge', 'marge %']}
                        totals={['tarif', 'montantAchat', 'marge']}
                        totalPosition="top"
                        headSubs={{ "numero": "numero billet/pnr", "dateVol": "date vol Aller", "tarif": "Montant Vente" }}
                    />
                )}
            </Paper>
        </Box>
    );
}

export default Journal;
